import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '../common/Button';
import { AuthCTA } from './AuthCTA';
import { AlertCircle, Eye, EyeOff, Lock, Mail, User } from 'lucide-react';
import { useAuth } from '../../context/auth';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import * as Sentry from '@sentry/react';
import { validatePassword } from '../../utils/validation';
import { get_terms_and_confidentiality_url } from '../../lib/site_config';
import { getAuthErrorTranslationKey, getEmailValidationKey } from '../../utils/authErrors';

interface SignUpFormProps {
  onComplete: ({ isConfirmed, email }: { isConfirmed: boolean; email: string | undefined }) => void;
  initialFirstName?: string;
  initialEmail?: string;
  lockEmail?: boolean;
  emailRedirectTo?: string;
}

export default function SignUpForm({
  onComplete,
  initialFirstName = '',
  initialEmail = '',
  lockEmail = false,
  emailRedirectTo,
}: SignUpFormProps) {
  const { t } = useTranslation(['common', 'auth', 'home']);
  const { signUp, doesEmailExist } = useAuth();
  const [searchParams] = useSearchParams();
  const initialFirstNameValue = initialFirstName || searchParams.get('firstName') || '';
  const initialEmailValue = initialEmail || searchParams.get('email') || '';
  const [firstName, setFirstName] = useState(initialFirstNameValue);
  const [email, setEmail] = useState(initialEmailValue);
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptNotifications, setAcceptNotifications] = useState(true);
  const [acceptNewsletter, setAcceptNewsletter] = useState(false);
  const [saving, setSaving] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);
  const [show, setShow] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [passwordMismatch, setPasswordMismatch] = useState(false);

  useEffect(() => {
    setFirstName(initialFirstNameValue);
  }, [initialFirstNameValue]);

  useEffect(() => {
    setEmail(initialEmailValue);
  }, [initialEmailValue]);

  const isFormValid =
    firstName &&
    email &&
    password &&
    passwordConfirm &&
    password === passwordConfirm &&
    acceptTerms;
  const terms_url = get_terms_and_confidentiality_url({
    section: 'terms',
  });
  const privacy_url = get_terms_and_confidentiality_url({
    section: 'privacy',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors = validatePassword(password);
    if (errors.length > 0) {
      setPasswordErrors(errors);
      return;
    } else {
      setPasswordErrors([]);
    }
    if (password !== passwordConfirm) {
      setPasswordMismatch(true);
      return;
    } else {
      setPasswordMismatch(false);
    }
    if (!isFormValid) {
      toast.error(t('errors.createAccount', { ns: 'auth' }));
      return;
    }

    const emailValidationKey = getEmailValidationKey(email);
    if (emailValidationKey) {
      setEmailError(t(emailValidationKey, { ns: 'auth' }));
      return;
    }

    setSaving(true);
    try {
      const normalizedEmail = email.trim();
      const emailExist = await doesEmailExist(normalizedEmail);
      if (emailExist === true) {
        setEmailError(t('emailTakenException', { ns: 'auth' }));
        return;
      }

      const result = await signUp(
        firstName,
        normalizedEmail,
        password,
        acceptNotifications,
        acceptNewsletter,
        emailRedirectTo
      );
      onComplete(result);
    } catch (err) {
      Sentry.captureException(err);
      toast.error(t(getAuthErrorTranslationKey(err, 'errors.accountCreation'), { ns: 'auth' }));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen">
      <div className="flex flex-col md:flex-row md:min-h-[calc(100vh-4rem)]">
        {/* Right side - Sign Up Form */}
        <div className="w-full md:w-1/2 order-1 md:order-2 pt-11 pb-8 md:px-8 md:flex">
          <div className="max-w-md w-full mx-auto">
            <h1 className="text-secondary [font-feature-settings:'liga'_off,'clig'_off] font-ambit text-[1.5625rem] font-[900] leading-[1.875rem] mb-2">
              {t('register.title', { ns: 'auth' })}
            </h1>
            <p className="text-[#3B5266] [font-feature-settings:'liga'_off,'clig'_off] font-ambit text-[1.0625rem] font-normal leading-[1.5625rem] mb-1">
              {t('hasAccount', { ns: 'auth' })}{' '}
            </p>
            <Link
              to="/login"
              className="text-[#3B5266] [font-feature-settings:'liga'_off,'clig'_off] font-ambit text-[1.0625rem] font-[700] leading-[1.5625rem] underline decoration-solid decoration-[#3B5266]"
            >
              {t('register.signInCta', { ns: 'auth' })}
            </Link>

            <form onSubmit={handleSubmit} className="mt-8" noValidate>
              <div className="mt-4">
                <label
                  htmlFor="firstName"
                  className="text-[#3B5266] [font-feature-settings:'liga'_off,'clig'_off] font-ambit text-[0.9375rem] font-normal leading-[1.25rem]"
                >
                  {t('firstName', { ns: 'auth' })}
                </label>
                <div className="relative mt-2">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className="h-5 w-5 text-secondary-light" />
                  </div>
                  <input
                    id="firstName"
                    type="text"
                    value={firstName}
                    onChange={e => setFirstName(e.target.value)}
                    className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-md bg-[#E1E9F0] focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder={t('firstNamePlaceholder', { ns: 'auth' })}
                    required
                  />
                </div>
              </div>

              <div className="mt-4">
                <label
                  htmlFor="email"
                  className="text-[#3B5266] [font-feature-settings:'liga'_off,'clig'_off] font-ambit text-[0.9375rem] font-normal leading-[1.25rem]"
                >
                  {t('email', { ns: 'auth' })}
                </label>
                <div className="relative mt-2">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-secondary-light" />
                  </div>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={e => {
                      if (!lockEmail) {
                        setEmail(e.target.value);
                      }
                      if (emailError) setEmailError('');
                    }}
                    className={`
                        block w-full pl-10 pr-3 py-3 rounded-md sm:text-sm
                        ${
                          emailError !== ''
                            ? 'border border-[#F04438] bg-[#FEE4E2]'
                            : 'border border-gray-300 bg-[#E1E9F0] focus:ring-blue-500 focus:border-blue-500'
                        }
                      `}
                    placeholder={t('emailPlaceholder', { ns: 'auth' })}
                    required
                    readOnly={lockEmail}
                  />
                </div>
                {emailError && <p className="font-ambit mt-2 text-[#F04438]">{emailError}</p>}
              </div>

              <div className="mt-4">
                <label
                  htmlFor="password"
                  className="text-[#3B5266] font-ambit text-[0.9375rem] leading-[1.25rem]"
                >
                  {t('password', { ns: 'auth' })}
                </label>

                <ul className="list-disc list-outside my-4 pl-10 text-secondary space-y-2">
                  <li>{t('errors.passwordTooShort', { ns: 'auth' })}</li>
                  <li>{t('errors.passwordNumberOrSpecial', { ns: 'auth' })}</li>
                  <li>{t('errors.passwordUpperLower', { ns: 'auth' })}</li>
                </ul>

                <div className="flex items-center mt-2 space-x-2">
                  <div className="relative flex-1">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Lock className="h-5 w-5 text-secondary-light" />
                    </div>
                    <input
                      id="password"
                      type={show ? 'text' : 'password'}
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      className={`
                        block w-full pl-10 pr-3 py-3 border rounded-md sm:text-sm
                        ${
                          passwordErrors.length > 0
                            ? 'border border-[#F04438] bg-[#FEE4E2]'
                            : 'border-gray-300 bg-[#E1E9F0] focus:ring-blue-500 focus:border-blue-500'
                        }
                      `}
                      placeholder={t('passwordPlaceholder', { ns: 'auth' })}
                      required
                    />
                    <button
                      type="button"
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-secondary-light"
                      onClick={() => setShow(v => !v)}
                      tabIndex={-1}
                    >
                      {show ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>

                  {passwordErrors.length > 0 && (
                    <AlertCircle className="text-red-500 w-5 h-5 shrink-0" />
                  )}
                </div>

                {passwordErrors.length > 0 && (
                  <ul className="mt-2 text-sm text-red-600 space-y-2">
                    {passwordErrors.map((err, idx) => (
                      <li key={idx} className="flex items-start">
                        {t(`errors.${err}`, { ns: 'auth' })}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="mt-4">
                <label
                  htmlFor="passwordConfirm"
                  className="text-[#3B5266] font-ambit text-[0.9375rem] leading-[1.25rem]"
                >
                  {t('confirmPassword', { ns: 'auth' })}
                </label>

                <div className="flex items-center mt-2 space-x-2">
                  <div className="relative flex-1">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Lock className="h-5 w-5 text-secondary-light" />
                    </div>
                    <input
                      id="passwordConfirm"
                      type={showConfirm ? 'text' : 'password'}
                      value={passwordConfirm}
                      onChange={e => {
                        setPasswordConfirm(e.target.value);
                        if (passwordMismatch && e.target.value === password) {
                          setPasswordMismatch(false);
                        }
                      }}
                      className={`
                        block w-full pl-10 pr-3 py-3 border rounded-md sm:text-sm
                        ${
                          passwordMismatch
                            ? 'border border-[#F04438] bg-[#FEE4E2]'
                            : 'border-gray-300 bg-[#E1E9F0] focus:ring-blue-500 focus:border-blue-500'
                        }
                      `}
                      placeholder={t('confirmPasswordPlaceholder', { ns: 'auth' })}
                      required
                    />
                    <button
                      type="button"
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-secondary-light"
                      onClick={() => setShowConfirm(v => !v)}
                      tabIndex={-1}
                    >
                      {showConfirm ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>

                  {passwordMismatch && <AlertCircle className="text-red-500 w-5 h-5 shrink-0" />}
                </div>

                {passwordMismatch && (
                  <p className="mt-2 text-sm text-red-600">
                    {t('errors.passwordMismatch', { ns: 'auth' })}
                  </p>
                )}
              </div>

              <div className="mt-6 space-y-6">
                <div className="bg-blue-50 p-4 rounded-md border border-blue-200">
                  <div className="mb-4">
                    <p className="text-[#3B5266] [font-feature-settings:'liga'_off,'clig'_off] font-ambit text-[0.875rem] font-normal leading-[1.375rem]">
                      <strong className="font-semibold">
                        {t('register.dataProtection.title', { ns: 'auth' })}
                      </strong>{' '}
                      {t('register.dataProtection.paragraph1', { ns: 'auth' })}
                      <br />
                      <br />
                      {t('register.dataProtection.paragraph2', { ns: 'auth' })}
                      <br />
                      <br />
                      {t('register.dataProtection.paragraph3', { ns: 'auth' })}
                    </p>
                  </div>

                  <label className="flex items-start">
                    <input
                      type="checkbox"
                      className="mt-1 h-4 w-4 text-secondary border-gray-300 rounded"
                      checked={acceptTerms}
                      onChange={e => setAcceptTerms(e.target.checked)}
                      required
                    />
                    <span className="ml-2 text-[#3B5266] [font-feature-settings:'liga'_off,'clig'_off] font-ambit text-[0.9375rem] font-normal leading-[1.25rem]">
                      {t('register.acceptTerms.prefix', { ns: 'auth' })}{' '}
                      <a
                        href={terms_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline decoration-solid decoration-[#3B5266]"
                      >
                        {t('register.acceptTerms.termsOfUse', { ns: 'auth' })}
                      </a>{' '}
                      {t('register.acceptTerms.and', { ns: 'auth' })}{' '}
                      <a
                        href={privacy_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline decoration-solid decoration-[#3B5266]"
                      >
                        {t('register.acceptTerms.privacyPolicy', { ns: 'auth' })}
                      </a>
                      {t('register.acceptTerms.suffix', { ns: 'auth' })}
                    </span>
                  </label>
                </div>

                <div>
                  <label className="flex items-start">
                    <input
                      type="checkbox"
                      className="mt-1 h-4 w-4 text-secondary border-gray-300 rounded"
                      checked={acceptNotifications}
                      onChange={e => setAcceptNotifications(e.target.checked)}
                    />
                    <span className="ml-2 text-[#3B5266] [font-feature-settings:'liga'_off,'clig'_off] font-ambit text-[0.9375rem] font-normal leading-[1.25rem]">
                      {t('register.acceptNotifications', { ns: 'auth' })}
                    </span>
                  </label>
                </div>

                <div>
                  <label className="flex items-start">
                    <input
                      type="checkbox"
                      className="mt-1 h-4 w-4 text-secondary border-gray-300 rounded"
                      checked={acceptNewsletter}
                      onChange={e => setAcceptNewsletter(e.target.checked)}
                    />
                    <span className="ml-2 text-[#3B5266] [font-feature-settings:'liga'_off,'clig'_off] font-ambit text-[0.9375rem] font-normal leading-[1.25rem]">
                      {t('register.acceptNewsletter', { ns: 'auth' })}
                    </span>
                  </label>
                </div>
              </div>

              <Button
                type="submit"
                name="signUp"
                variant="primary"
                className="w-full md:w-auto md:min-w-60 mt-8 mb-4 px-12 py-2"
                disabled={!isFormValid || saving}
              >
                {saving ? t('signUpCtaSubmitting', { ns: 'auth' }) : t('signUpCta', { ns: 'auth' })}
              </Button>
            </form>
          </div>
        </div>
        {/* CTA Block - always visible, after form on mobile, left on desktop */}
        <AuthCTA
          slogan={t('slogan', { ns: 'home' })}
          ctaTitle={t('ctaTitle', { ns: 'home' })}
          ctaSubtitle={t('ctaSubtitle', { ns: 'home' })}
          buttonText={t('discoverButton', { ns: 'home' })}
        />
      </div>
    </div>
  );
}
